'use client';

import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import Skeleton from '~/components/Loading/Skeleton';
import { cn } from '~/lib/utils';
import { useHomeQueueOverview } from './useHomeQueueOverview';

type HomeChartsProps = {
  canViewReviewQueue: boolean;
  canViewContentScanner: boolean;
};

function ChartCardSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'border-border/80 bg-card overflow-hidden rounded-xl border shadow-[0_1px_2px_rgba(0,0,0,0.04)]',
        className,
      )}
    >
      <div className="space-y-1 px-5 pt-4 pb-2">
        <Skeleton className="h-4 w-28" />
      </div>
      <div className="px-5 pb-4">
        <Skeleton className="h-[200px] w-full rounded-lg" />
      </div>
    </div>
  );
}

const ContentChartShadcn = dynamic(
  () => import('../../content-scanner/_components/Dashboard/Charts/ContentChartShadcn'),
  { loading: () => <ChartCardSkeleton /> },
);

const RiskChartShadcn = dynamic(
  () => import('../../content-scanner/_components/Dashboard/Charts/RiskChartShadcn'),
  { loading: () => <ChartCardSkeleton /> },
);

const ShadcnBarChart = dynamic(
  () => import('../../content-scanner/_components/Dashboard/Charts/ShadcnBarChart'),
  { loading: () => <ChartCardSkeleton /> },
);

const QUEUE_CHART_COLORS = [
  'rgb(var(--chart-2))',
  'rgb(var(--chart-1))',
  'rgb(var(--chart-5))',
  'rgb(var(--chart-3))',
  'rgb(var(--chart-4))',
  '#71717a',
];

const chartCardClassName =
  'transition-shadow duration-150 hover:shadow-[0_4px_14px_rgba(0,0,0,0.07)]';

export default function HomeCharts({ canViewReviewQueue, canViewContentScanner }: HomeChartsProps) {
  const { data: overview, isLoading: isReviewLoading } = useHomeQueueOverview(canViewReviewQueue);

  const reviewSeries = overview?.series ?? [];
  const reviewChartData = useMemo(() => {
    const volume = overview?.volume ?? [];
    if (!reviewSeries.length) {
      return volume.map((row) => ({
        name: row.name,
        date: row.date,
        reviews: 0,
      }));
    }
    return volume.map((row) => ({
      name: row.name,
      date: row.date,
      ...row.counts,
    }));
  }, [overview?.volume, reviewSeries]);

  const reviewColorMap = useMemo(() => {
    if (!reviewSeries.length) {
      return { reviews: QUEUE_CHART_COLORS[0]! };
    }
    return Object.fromEntries(
      reviewSeries.map((item, index) => [
        item.key,
        QUEUE_CHART_COLORS[index] ?? QUEUE_CHART_COLORS[QUEUE_CHART_COLORS.length - 1]!,
      ]),
    ) as Record<string, string>;
  }, [reviewSeries]);

  const reviewLabelOverrides = useMemo(
    () => Object.fromEntries(reviewSeries.map((item) => [item.key, item.label])),
    [reviewSeries],
  );

  const showReviewChart =
    canViewReviewQueue && (isReviewLoading || (overview?.queues.length ?? 0) > 0);
  const showContentChart = canViewContentScanner;
  const showRiskChart = canViewContentScanner;
  const showVolumePair = showReviewChart || showContentChart;

  if (!showRiskChart && !showVolumePair) return null;

  const volumePairClassName = cn(
    'grid grid-cols-1 gap-3 lg:gap-4 [&>*]:min-w-0',
    showReviewChart && showContentChart && 'lg:grid-cols-2',
  );

  return (
    <section aria-label="Trends" className="space-y-3 lg:space-y-4">
      {showRiskChart ? (
        <RiskChartShadcn height={200} label="New Risks" className={chartCardClassName} />
      ) : null}

      {showVolumePair ? (
        <div className={volumePairClassName}>
          {showContentChart ? (
            <ContentChartShadcn
              height={200}
              label="New Content"
              className={chartCardClassName}
            />
          ) : null}

          {showReviewChart ? (
            isReviewLoading ? (
              <ChartCardSkeleton />
            ) : (
              <ShadcnBarChart
                height={200}
                title="New Reviews"
                className={chartCardClassName}
                data={reviewChartData}
                colorMap={reviewColorMap}
                labelOverrides={reviewLabelOverrides}
                stackedMode
                onlyShowActiveDataKeys
              />
            )
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
